import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import videojsModule from 'video.js';
import 'video.js/dist/video-js.css';
import deLanguage from 'video.js/dist/lang/de.json' with { type: 'json' };
import enLanguage from 'video.js/dist/lang/en.json' with { type: 'json' };
import trLanguage from 'video.js/dist/lang/tr.json' with { type: 'json' };
import type { GlobusCaptionLanguage, GlobusCaptionTrack } from '../../shared/content/globusJobContent.js';

interface VideoJsPlayer {
  addRemoteTextTrack(options: Record<string, unknown>, manualCleanup?: boolean): object;
  dispose(): void;
  el(): Element;
  isDisposed(): boolean;
  language(code?: string): string | undefined;
  one(event: string, callback: () => void): void;
  poster(source: string): unknown;
  removeRemoteTextTrack(track: object): void;
  src(source: Record<string, unknown> | Record<string, unknown>[]): unknown;
}

// video.js publishes a callable default export, while its NodeNext type entry is
// exposed as a module namespace. Keep the runtime import intact and describe the
// callable surface explicitly for strict TypeScript builds.
interface VideoJsFactory {
  (element: string | Element, options?: Record<string, unknown>): VideoJsPlayer;
  addLanguage(code: string, data: Record<string, string>): void;
}

const videojs = videojsModule as unknown as VideoJsFactory;

const videoLanguages = {
  de: {
    ...deLanguage,
    'subtitles settings': 'Untertitel-Einstellungen',
  },
  en: enLanguage,
  tr: {
    ...trLanguage,
    'subtitles settings': 'Altyazı ayarları',
    'subtitles off': 'Altyazılar kapalı',
    Color: 'Renk',
    Opacity: 'Opaklık',
    'Text Background': 'Metin arka planı',
    'Caption Area Background': 'Altyazı alanı arka planı',
    'Playing in Picture-in-Picture': 'Mini oynatıcıda oynatılıyor',
  },
} satisfies Record<GlobusVideoPlayerProps['locale'], Record<string, string>>;

Object.entries(videoLanguages).forEach(([locale, language]) => videojs.addLanguage(locale, language));

const captionSettingsLabels = {
  de: { title: 'Untertitel-Einstellungen', dialog: 'Einstellungsdialog für Untertitel' },
  en: { title: 'Subtitle settings', dialog: 'Subtitle settings dialog' },
  tr: { title: 'Altyazı ayarları', dialog: 'Altyazı ayarları iletişim kutusu' },
} satisfies Record<GlobusVideoPlayerProps['locale'], { title: string; dialog: string }>;

interface GlobusVideoPlayerProps {
  id: string;
  src: string;
  poster: string;
  title: string;
  captionTracks: GlobusCaptionTrack[];
  locale: 'de' | 'en' | 'tr';
  unavailableText: string;
  loadingText: string;
  eager?: boolean;
}

const videoMimeType = (source: string) => (source.toLowerCase().includes('.webm') ? 'video/webm' : 'video/mp4');
const emptyWebVttSource =
  'data:text/vtt;charset=utf-8,WEBVTT%0A%0A23%3A59%3A58.000%20--%3E%2023%3A59%3A59.000%0A%E2%80%8B%0A';

const captionLabels: Record<GlobusVideoPlayerProps['locale'], Record<GlobusCaptionLanguage, string>> = {
  de: { de: 'Deutsch', en: 'Englisch', tr: 'Türkisch' },
  en: { de: 'German', en: 'English', tr: 'Turkish' },
  tr: { de: 'Almanca', en: 'İngilizce', tr: 'Türkçe' },
};

const fallbackCaptionTracks = (locale: GlobusVideoPlayerProps['locale']): GlobusCaptionTrack[] =>
  (['de', 'en', 'tr'] as const).map(srclang => ({
    src: '',
    srclang,
    label: captionLabels[locale][srclang],
  }));

export const globusVideoPlayer = stateful((props: GlobusVideoPlayerProps) => {
  const state = useMemo(
    () => [
      {
        container: createRef<HTMLElement>(),
        playerElement: createRef<HTMLElement>(),
        player: undefined as ReturnType<typeof videojs> | undefined,
        captionTracks: [] as object[],
        captionSyncId: 0,
      },
    ],
    [],
  );
  const [isNearViewport, setIsNearViewport] = useState(props.eager === true, [props.id]);

  const synchronizePlayer = (player: VideoJsPlayer) => {
    player.language(props.locale);
    const playerElement = player.el();
    playerElement.setAttribute('lang', props.locale);
    const settingsDialog = playerElement.querySelector<HTMLElement>('.vjs-modal-dialog.vjs-text-track-settings');
    const settingsContent = settingsDialog?.querySelector<HTMLElement>('.vjs-modal-dialog-content');
    const settingsLabels = captionSettingsLabels[props.locale];
    if (settingsDialog && settingsContent) {
      settingsDialog.setAttribute('aria-label', settingsLabels.dialog);
      let settingsTitle = settingsContent.querySelector<HTMLElement>('.vp-globus-caption-settings-title');
      if (!settingsTitle) {
        settingsTitle = document.createElement('h3');
        settingsTitle.className = 'vp-globus-caption-settings-title';
        settingsContent.prepend(settingsTitle);
      }
      settingsTitle.textContent = settingsLabels.title;
    }
    player.poster(props.poster || '');
    state.captionTracks.forEach(track => player.removeRemoteTextTrack(track));
    state.captionTracks = [];
    const captionSyncId = ++state.captionSyncId;
    if (!props.src) {
      player.src([]);
      return;
    }
    const captionTracks = props.captionTracks.length ? props.captionTracks : fallbackCaptionTracks(props.locale);
    const hasExplicitDefault = captionTracks.some(track => track.src.trim() && track.default === true);
    player.one('loadstart', () => {
      if (state.captionSyncId !== captionSyncId || player.isDisposed()) return;
      state.captionTracks = captionTracks.map(track =>
        player.addRemoteTextTrack(
          {
            src: track.src.trim() || emptyWebVttSource,
            kind: 'subtitles',
            srclang: track.srclang,
            label: track.label,
            default:
              Boolean(track.src.trim()) &&
              (track.default === true || (!hasExplicitDefault && track.srclang === props.locale)),
          },
          true,
        ),
      );
    });
    player.src({ src: props.src, type: videoMimeType(props.src) });
  };

  useEffect(() => {
    if (props.eager || isNearViewport || typeof IntersectionObserver === 'undefined') {
      if (!isNearViewport) setIsNearViewport(true);
      return;
    }
    const container = state.container.value;
    if (!container) return;
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        setIsNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: '480px 0px' },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [props.eager, props.id, isNearViewport]);

  useEffect(() => {
    if (!isNearViewport || state.player) return;
    const playerElement = state.playerElement.value;
    if (!playerElement) return;

    const player = videojs(playerElement, {
      autoplay: false,
      controls: true,
      fluid: true,
      responsive: true,
      aspectRatio: '16:9',
      playsinline: true,
      preload: 'metadata',
      poster: props.poster || undefined,
      language: props.locale,
      html5: { nativeTextTracks: false },
      controlBar: {
        children: [
          'playToggle',
          'volumePanel',
          'progressControl',
          'remainingTimeDisplay',
          'subsCapsButton',
          'pictureInPictureToggle',
          'fullscreenToggle',
        ],
        volumePanel: { inline: true },
      },
      sources: props.src ? [{ src: props.src, type: videoMimeType(props.src) }] : [],
    });
    state.player = player;
    synchronizePlayer(player);

    return () => {
      state.captionTracks = [];
      state.player = undefined;
      if (!player.isDisposed()) player.dispose();
    };
  }, [isNearViewport, props.id]);

  useEffect(() => {
    const player = state.player;
    if (!player || player.isDisposed()) return;
    synchronizePlayer(player);
  }, [
    isNearViewport,
    props.src,
    props.poster,
    props.locale,
    props.captionTracks.map(track => `${track.srclang}:${track.label}:${track.src}:${track.default === true}`).join('|'),
  ]);

  return html`
    <div
      class="vp-globus-video-frame"
      ${ref(state.container)}
      data-video-state=${!props.src ? 'unavailable' : isNearViewport ? 'ready' : 'deferred'}
    >
      ${isNearViewport
        ? html`
              <div class="vp-globus-video-player" data-vjs-player>
                <video-js
                  ${ref(state.playerElement)}
                  class="video-js vjs-big-play-centered vp-globus-video-js"
                  controls
                  playsinline
                  preload="metadata"
                  aria-label=${props.title}
                ></video-js>
              </div>
              ${!props.src
                ? html`<div class="vp-globus-video-message" role="status">
                    <span class="icon-[material-symbols--videocam-off-outline]" aria-hidden="true"></span>
                    <span>${props.unavailableText}</span>
                  </div>`
                : ''}
            `
        : html`
              ${props.poster
                ? html`<img class="vp-globus-video-poster" src=${props.poster} alt=${props.title} loading="lazy" decoding="async" />`
                : ''}
              ${!props.src
                ? html`<div class="vp-globus-video-message" role="status">
                    <span class="icon-[material-symbols--videocam-off-outline]" aria-hidden="true"></span>
                    <span>${props.unavailableText}</span>
                  </div>`
                : html`<span class="vp-visually-hidden" role="status">${props.loadingText}</span>`}
            `}
    </div>
  `;
});
